import { useState } from "react";
import { useParams } from "react-router";
import { sendWithFile } from "../../../common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { isNullOrWhitespace } from "@common/stringOperations";
import { Result } from "../../../common/api/result";
import { DotLegalSelectOption } from "../../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useAllSelectableUsersQuery, useSelectableDocumentTypesQueryByUsageType } from "../../../common/hooks/useSelectableItemQueries";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useValidator } from "../../../common/hooks/validation";
import { useTranslation } from "../../../localization/useTranslation";
import { LegalEntityDocumentUpdateModel } from "../LegalEntityDocumentTab.types";
import { IAddLegalEntityDocumentDialogProps } from "./AddLegalEntityDocumentDialog";
import { useDocumentHook } from "../../../documents/Documents.hook";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { DocumentTypeUsageType } from "../../../masterData/documentType/documentTypes/DocumentTypes.types";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useAddLegalEntityDocumentHook(props: IAddLegalEntityDocumentDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { gdpo } = useUserContext();
    const { translateString } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const [document, setDocument] = useState<LegalEntityDocumentUpdateModel | undefined>(new LegalEntityDocumentUpdateModel(gdpo || props.isGroupEntity));
    const [documentationOption, setDocumentationOption] = useState(1);
    const [file, setFile] = useState<File>();
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();
    const { data, isLoading } = useAllSelectableUsersQuery();
    const { data: documentTypes, isLoading: documentTypesIsLoading } = useSelectableDocumentTypesQueryByUsageType(DocumentTypeUsageType.AllTransfers);

    const validation = useValidator<LegalEntityDocumentUpdateModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            fileName: documentationOption === 0 ? undefined : validators.validateNotEmpty((item) => item.fileName, translateString("file")),
            link: !document?.isLink && documentationOption === 1 ? undefined : validators.validateUrlAndNotEmpty((item) => item.link, translateString("link")),
            documentTypeId: validators.validateNotEmpty((item) => item.documentTypeId, translateString("type")),
            status: validators.validateNotEmpty((item) => item.status, translateString("status")),
            groupEntityAccessIds: validators.validateNotEmpty(
                (item) => item.groupEntityAccessIds,
                "",
                (item) => item.allGroupEntities,
                translateString("oneGroupCompanyIsRequired")
            ),
        }),
        [documentationOption]
    );

    let documentStatus = useDocumentHook().selecableDocumentStatus;

    const createMutation = useDotLegalMutation(getCreateMutation);

    const createDocument = () => {
        createMutation.mutateAsync(document!, {
            onSuccess: (response: Result<string>) => {
                snackbar.show(translateString("documentCreated"));
                props.onCloseDialog();
            },
        });
    };

    function onDocumentationTypeChange(option: number) {
        let tempModel = { ...document! };
        if (option === 0) {
            setFile(undefined);
            tempModel.fileName = "";
            setDocument(tempModel);
            setDocumentationOption(0);
        }

        if (option === 1) {
            tempModel.link = undefined;
            setDocument(tempModel);
            setDocumentationOption(1);
        }
    }

    function onFilesChanged(files: Array<DotLegalFile> | undefined) {
        let tempDoc = { ...document! };
        setFile(files?.[0].file);
        tempDoc.fileName = files ? files[0].fileName : "";

        if (isNullOrWhitespace(tempDoc.name)) {
            tempDoc.name = tempDoc.fileName.substring(0, tempDoc.fileName.lastIndexOf("."));
        }

        setDocument(tempDoc);
    }

    function onGroupEntityAccessIdsChange(groupEntityIds: Array<string>) {
        let tempModel = { ...document! };
        tempModel.groupEntityAccessIds = groupEntityIds;

        if (groupEntityIds.length > 0) {
            tempModel.allGroupEntities = false;
        }

        setDocument(tempModel);
    }

    function onAllGroupEntitiesChange(allGroupEntities: boolean) {
        let tempModel = { ...document! };
        tempModel.allGroupEntities = allGroupEntities;

        if (allGroupEntities) {
            tempModel.groupEntityAccessIds = [];
        }

        setDocument(tempModel);
    }

    return {
        document,
        setDocument,
        documentTypes,
        inProgress: createMutation.isLoading,
        isUploading: createMutation.isLoading && documentationOption === 1,
        uploadPercentage,
        onFilesChanged,
        validation,
        validateDocumentForm,
        userOptions: data,
        userOptionsIsLoading: isLoading,
        documentationOption,
        onDocumentationTypeChange,
        documentStatus,
        onGroupEntityAccessIdsChange,
        onAllGroupEntitiesChange,
        gdpo,
        documentTypesIsLoading,
    };

    function getCreateMutation(doc: LegalEntityDocumentUpdateModel) {
        return sendWithFile<string>("POST", `/LegalEntity/${id}/Documents`, doc, file, trackUploadProgress());
    }

    function validateDocumentForm() {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        createDocument();
    }
}
